import { useMemo } from "react";
import type { AppShellSidebarGroup } from "@musa.tech/design-system";
import { useAuthContext } from "@/components/AuthProvider";
import { usePermissions } from "@/hooks/useUnifiedAuth";
import {
  filterNavigationGroups,
  type PermissionRole,
} from "@/components/layout/navigation";

interface UseSidebarNavigationResult {
  groups: AppShellSidebarGroup[];
  isLoading: boolean;
}

/**
 * Grupos do menu lateral já filtrados para o usuário — GR-637.
 *
 * Quem manda é a permissão do catálogo (client role do `portal-gerador`), que
 * chega pronta no token. Enquanto o perfil carrega, o menu fica em skeleton;
 * quem ainda não foi migrado para os grupos continua no filtro legado por
 * papel. A ordem de precedência vive em `filterNavigationGroups`.
 *
 * O `href` repete a rota do item para o menu renderizar links de verdade —
 * Cmd/Ctrl+clique abre em nova aba; o clique simples segue no client-side.
 *
 * Esconder o item NÃO bloqueia a rota: `ProtectedRoute` só valida
 * autenticação, e a tela continua alcançável por URL, bookmark ou deep link.
 * O bloqueio de rota é etapa própria.
 */
export const useSidebarNavigation = (): UseSidebarNavigationResult => {
  const { profile, isLoading } = useAuthContext();
  const { permissions, isMigrated, isInternal } = usePermissions();
  const role = (profile?.permission_role ?? null) as PermissionRole | null;

  const groups = useMemo<AppShellSidebarGroup[]>(
    () =>
      filterNavigationGroups(role, {
        permissions,
        isMigrated,
        isInternal,
      }).map((group) => ({
        ...group,
        items: group.items.map((item) => ({ ...item, href: item.id })),
      })),
    [role, permissions, isMigrated, isInternal],
  );

  return { groups, isLoading };
};
